import { GroupCreateEvent } from '#src/groups/analytics/group_analytics_events.ts';
import { AddExclusionsStep } from '#src/pages/create/add_exclusions_step/add_exclusions_step.tsx';
import { AddParticipantsStep } from '#src/pages/create/add_participants_step/add_participants_step.tsx';
import { useParticipantsList } from '#src/pages/create/add_participants_step/use_participants_list.ts';
import { buildCreateGroupRequest } from '#src/pages/create/build_create_group_request.ts';
import { NameAndSubmitStep } from '#src/pages/create/name_and_submit_step/name_and_submit_step.tsx';
import type { FormStep } from '#src/pages/create/types.ts';
import { FormSteps } from '#src/pages/create/types.ts';
import { CreateGroupError, useCreateGroup } from '#src/pages/create/use_create_group.ts';
import { useServices } from '#src/services/services.ts';
import { PageLayout } from '#src/ui/layout/page_layout.tsx';
import { getCreateGroupSuccessHref } from '#src/ui/navigation/navigation.ts';
import { useDocumentTitle } from '#src/ui/navigation/use_document_title.ts';
import { UnreachableError } from '@packages/primitives/unreachable_error';
import { Card, CardLogo } from '@packages/ui/base/card/card';
import { ProgressBar } from '@packages/ui/base/progress/progress_bar';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

const PAGE_TITLE = 'Create Your Secret Santa Group - No Signup | draw.name';
const STEP_SEARCH_PARAM = 'step';
const DEFAULT_GROUP_NAME = `Secret Santa ${new Date().getFullYear()}`;

export const CreatePage = () => {
  useDocumentTitle(PAGE_TITLE);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { captureEvent, errorService, groupClient } = useServices();
  const createGroup = useCreateGroup(groupClient);

  const {
    participants,
    validationError,
    duplicateParticipantIds,
    isParticipantsEmpty,
    focusParticipantId,
    validateParticipants,
    onAppendParticipant,
    onInsertParticipant,
    onDeleteParticipant,
    onUpdateParticipantName,
    onUpdateParticipantExclusions,
    clearEmptyParticipants,
  } = useParticipantsList();

  const [wantsToAddExclusions, setWantsToAddExclusions] = useState(false);
  const [groupName, setGroupName] = useState('');

  const stepSearchParam = searchParams.get(STEP_SEARCH_PARAM);
  const currentStep = (stepSearchParam || FormSteps.ADD_PARTICIPANTS) as FormStep;

  // Always have participants when on any other step otherwise go back.
  useEffect(() => {
    if (currentStep !== FormSteps.ADD_PARTICIPANTS && isParticipantsEmpty) {
      setSearchParams(prev => {
        prev.set(STEP_SEARCH_PARAM, FormSteps.ADD_PARTICIPANTS);
        return prev;
      });
    }
  }, [currentStep, isParticipantsEmpty, setSearchParams]);

  const goToStep = (step: FormStep) => {
    setSearchParams(prev => {
      prev.set(STEP_SEARCH_PARAM, step);
      return prev;
    });
  };

  const onParticipantsNext = () => {
    const isValid = validateParticipants();
    if (isValid) {
      goToStep(FormSteps.ADD_EXCLUSIONS);
      clearEmptyParticipants();
    }
  };

  const onSubmit = async () => {
    const { request, exclusionCount } = buildCreateGroupRequest({
      name: groupName,
      participants,
      wantsToAddExclusions,
      defaultName: DEFAULT_GROUP_NAME,
    });
    const eventProperties = {
      hasCustomName: groupName.trim().length > 0,
      participantCount: participants.length,
      exclusionCount,
    };
    try {
      const response = await createGroup.mutateAsync(request);
      captureEvent(GroupCreateEvent, { isSuccess: true, ...eventProperties });
      await navigate(getCreateGroupSuccessHref(), { state: { groupId: response.id } });
    } catch (error) {
      captureEvent(GroupCreateEvent, { isSuccess: false, ...eventProperties });
      // Known rejections are shown to the organizer and are not unexpected errors.
      if (!(error instanceof CreateGroupError)) {
        errorService.captureError(error, { message: 'Failed to create group' });
      }
    }
  };

  return (
    <PageLayout scrolling="viewport">
      <Card className="flex max-h-full max-w-xl flex-col overflow-hidden">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <CardLogo />
          {/* Added margin to visually align since flex centering does not look right here */}
          <ProgressBar value={stepToProgressValue(currentStep)} className="mt-2" />
        </div>
        {/* Form */}
        {currentStep === FormSteps.ADD_PARTICIPANTS && (
          <AddParticipantsStep
            participants={participants}
            error={validationError}
            duplicateParticipantIds={duplicateParticipantIds}
            focusParticipantId={focusParticipantId}
            onAppendParticipant={onAppendParticipant}
            onInsertParticipant={onInsertParticipant}
            onDeleteParticipant={onDeleteParticipant}
            onUpdateParticipantName={onUpdateParticipantName}
            onNext={onParticipantsNext}
          />
        )}
        {currentStep === FormSteps.ADD_EXCLUSIONS && (
          <AddExclusionsStep
            participants={participants}
            wantsToAddExclusions={wantsToAddExclusions}
            onWantsToAddExclusionsChange={setWantsToAddExclusions}
            onUpdateParticipantExclusions={onUpdateParticipantExclusions}
            onNext={() => goToStep(FormSteps.ADD_NAME_AND_SUBMIT)}
            onBack={() => goToStep(FormSteps.ADD_PARTICIPANTS)}
          />
        )}
        {currentStep === FormSteps.ADD_NAME_AND_SUBMIT && (
          <NameAndSubmitStep
            name={groupName}
            submitting={createGroup.isPending}
            error={createGroup.displayErrorMessage}
            onNameChange={setGroupName}
            onBack={() => goToStep(FormSteps.ADD_EXCLUSIONS)}
            onSubmit={onSubmit}
          />
        )}
      </Card>
    </PageLayout>
  );
};

function stepToProgressValue(step: FormStep): number {
  switch (step) {
    case FormSteps.ADD_PARTICIPANTS:
      return 20;
    case FormSteps.ADD_EXCLUSIONS:
      return 60;
    case FormSteps.ADD_NAME_AND_SUBMIT:
      return 90;
    default:
      throw new UnreachableError(step);
  }
}
